import { useEffect, useState } from "react";
import { useParams, Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  ArrowLeft, 
  Clock, 
  BookOpen, 
  AlertTriangle, 
  Lightbulb,
  CheckCircle,
  HelpCircle,
  ChevronDown,
  ChevronUp,
  ExternalLink
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { getArticleBySlug, getCategoryBySlug } from "./content";
import { ArticleSection, HelpArticle, HelpCategory } from "@shared/schema";
import NotFound from "@/pages/not-found";
import Navigation from "@/components/Navigation";
import Footer from "@/components/ui/footer";

interface ArticleRendererProps {
  section: ArticleSection;
  index: number;
}

function ArticleRenderer({ section, index }: ArticleRendererProps) {
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);

  const toggleFaq = (faqIndex: number) => {
    setExpandedFaq(expandedFaq === faqIndex ? null : faqIndex);
  };

  switch (section.kind) {
    case 'p':
      return (
        <motion.p 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.1 }}
          className="text-gray-700 dark:text-gray-300 leading-relaxed mb-6"
          data-testid={`article-paragraph-${index}`}
        >
          {section.content as string}
        </motion.p>
      );

    case 'h2':
      return (
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.1 }}
          className="text-2xl font-bold text-gray-900 dark:text-white mb-4 mt-8 border-b border-gray-200 dark:border-gray-700 pb-2"
          data-testid={`article-heading-${index}`}
        >
          {section.content as string}
        </motion.h2>
      );

    case 'steps':
      return (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.1 }}
          className="mb-6"
          data-testid={`article-steps-${index}`}
        >
          <ol className="space-y-3">
            {(section.content as string[]).map((step, stepIndex) => (
              <li key={stepIndex} className="flex items-start space-x-3">
                <span className="flex-shrink-0 w-6 h-6 bg-purple-100 dark:bg-purple-900 text-purple-600 dark:text-purple-300 rounded-full flex items-center justify-center text-sm font-semibold">
                  {stepIndex + 1}
                </span>
                <span className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  {step}
                </span>
              </li>
            ))}
          </ol>
        </motion.div>
      );

    case 'tip':
      return (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: index * 0.1 }}
          className="mb-6"
          data-testid={`article-tip-${index}`}
        >
          <Card className="border-l-4 border-l-blue-500 bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800">
            <CardContent className="p-4">
              <div className="flex items-start space-x-3">
                <Lightbulb className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-blue-800 dark:text-blue-200 mb-2">
                    Pro Tip
                  </h4>
                  <p className="text-blue-700 dark:text-blue-300 leading-relaxed">
                    {section.content as string}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      );

    case 'warning':
      return (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: index * 0.1 }}
          className="mb-6"
          data-testid={`article-warning-${index}`}
        >
          <Card className="border-l-4 border-l-amber-500 bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800">
            <CardContent className="p-4">
              <div className="flex items-start space-x-3">
                <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-amber-800 dark:text-amber-200 mb-2">
                    Important Note
                  </h4>
                  <p className="text-amber-700 dark:text-amber-300 leading-relaxed">
                    {section.content as string}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      );

    case 'faq':
      return (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.1 }}
          className="mb-6"
          data-testid={`article-faq-${index}`}
        >
          <Card className="border border-gray-200 dark:border-gray-700">
            <CardContent className="p-4">
              <div className="flex items-center space-x-2 mb-4">
                <HelpCircle className="w-5 h-5 text-purple-500" />
                <h4 className="font-semibold text-gray-900 dark:text-white">
                  Frequently Asked Questions
                </h4>
              </div>
              <div className="space-y-2">
                {(section.content as string[]).map((faqItem, faqIndex) => {
                  const [question, answer] = faqItem.split('|');
                  return (
                    <div key={faqIndex} className="border border-gray-100 dark:border-gray-800 rounded-lg">
                      <button
                        onClick={() => toggleFaq(faqIndex)}
                        className="w-full px-4 py-3 text-left flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors rounded-lg"
                        data-testid={`faq-question-${faqIndex}`}
                      >
                        <span className="font-medium text-gray-900 dark:text-white pr-4">
                          {question}
                        </span>
                        {expandedFaq === faqIndex ? (
                          <ChevronUp className="w-4 h-4 text-gray-500 flex-shrink-0" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-gray-500 flex-shrink-0" />
                        )}
                      </button>
                      <AnimatePresence>
                        {expandedFaq === faqIndex && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.2 }}
                            className="border-t border-gray-100 dark:border-gray-800"
                            data-testid={`faq-answer-${faqIndex}`}
                          >
                            <div className="px-4 py-3 text-gray-700 dark:text-gray-300 leading-relaxed">
                              {answer}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      );

    default:
      return null;
  }
}

interface DifficultyBadgeProps {
  difficulty: string;
}

function DifficultyBadge({ difficulty }: DifficultyBadgeProps) {
  const colors = {
    beginner: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
    intermediate: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300",
    advanced: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300"
  };

  return (
    <Badge className={colors[difficulty as keyof typeof colors] || colors.beginner}>
      {difficulty.charAt(0).toUpperCase() + difficulty.slice(1)}
    </Badge>
  );
}

export default function Article() {
  const params = useParams();
  const [, setLocation] = useLocation();
  const [article, setArticle] = useState<HelpArticle | null>(null);
  const [category, setCategory] = useState<HelpCategory | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const categorySlug = params.category;
    const articleSlug = params.article;

    if (!categorySlug || !articleSlug) {
      setLoading(false);
      return;
    }

    // Find the article and category
    const foundArticle = getArticleBySlug(categorySlug, articleSlug);
    const foundCategory = getCategoryBySlug(categorySlug);

    setArticle(foundArticle || null);
    setCategory(foundCategory || null);
    setLoading(false);

    // Update page title and meta description for SEO
    if (foundArticle) {
      document.title = foundArticle.meta.titleTag;
      
      // Update meta description
      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.setAttribute('name', 'description');
        document.head.appendChild(metaDescription);
      }
      metaDescription.setAttribute('content', foundArticle.meta.description);

      // Update Open Graph tags
      let ogTitle = document.querySelector('meta[property="og:title"]');
      if (!ogTitle) {
        ogTitle = document.createElement('meta');
        ogTitle.setAttribute('property', 'og:title');
        document.head.appendChild(ogTitle);
      }
      ogTitle.setAttribute('content', foundArticle.meta.titleTag);

      let ogDescription = document.querySelector('meta[property="og:description"]');
      if (!ogDescription) {
        ogDescription = document.createElement('meta');
        ogDescription.setAttribute('property', 'og:description');
        document.head.appendChild(ogDescription);
      }
      ogDescription.setAttribute('content', foundArticle.meta.description);

      if (foundArticle.meta.ogImage) {
        let ogImage = document.querySelector('meta[property="og:image"]');
        if (!ogImage) {
          ogImage = document.createElement('meta');
          ogImage.setAttribute('property', 'og:image');
          document.head.appendChild(ogImage);
        }
        ogImage.setAttribute('content', foundArticle.meta.ogImage);
      }
    }
  }, [params.category, params.article]);

  // Clean up SEO tags when component unmounts
  useEffect(() => {
    return () => {
      document.title = "SnuggleCrafters - Personalized Bedtime Stories";
      
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', 'Create magical, personalized bedtime stories for your child with AI-powered storytelling.');
      }
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-teal-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center">
        <div className="text-center" data-testid="article-loading">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading article...</p>
        </div>
      </div>
    );
  }

  if (!article || !category) {
    return <NotFound />;
  }

  return (
    <>
      <Navigation />
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-teal-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 pt-16">
        <div className="container mx-auto px-4 py-8 max-w-4xl">
          {/* Breadcrumb Navigation */}
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
            data-testid="article-breadcrumb"
          >
            <nav className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
              <Link 
                href="/help" 
                className="hover:text-purple-600 dark:hover:text-purple-400 transition-colors"
                data-testid="breadcrumb-help"
              >
                Help Center
              </Link>
              <span>/</span>
              <Link 
                href="/help" 
                className="hover:text-purple-600 dark:hover:text-purple-400 transition-colors"
                data-testid="breadcrumb-category"
              >
                {category.title}
              </Link>
              <span>/</span>
              <span className="text-gray-900 dark:text-white font-medium">
                {article.title}
              </span>
            </nav>
          </motion.div>

        {/* Back Button */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="mb-6"
        >
          <Button
            variant="ghost"
            onClick={() => setLocation("/help")}
            className="text-gray-600 dark:text-gray-400 hover:text-purple-600 dark:hover:text-purple-400"
            data-testid="back-to-help"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Help Center
          </Button>
        </motion.div>

        {/* Article Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
          data-testid="article-header"
        >
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <Badge 
                variant="secondary" 
                className="bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300"
                data-testid="article-type-badge"
              >
                <BookOpen className="w-3 h-3 mr-1" />
                {article.type === 'guide' ? 'Guide' : 'Troubleshooting'}
              </Badge>
              <DifficultyBadge difficulty={article.difficulty} />
              <Badge variant="outline" className="text-gray-600 dark:text-gray-400" data-testid="article-time-badge">
                <Clock className="w-3 h-3 mr-1" />
                {article.estimatedTime}
              </Badge>
            </div>
            
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-3" data-testid="article-title">
              {article.title}
            </h1>
            
            <p className="text-lg text-gray-600 dark:text-gray-400 leading-relaxed" data-testid="article-description">
              {article.description}
            </p>
          </div>
        </motion.div>

        {/* Article Content */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-8"
          data-testid="article-content"
        >
          <div className="prose prose-lg max-w-none">
            {article.body.map((section, index) => (
              <ArticleRenderer key={index} section={section} index={index} />
            ))}
          </div>
        </motion.div>

        {/* Help Footer */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6 text-center"
          data-testid="article-footer"
        >
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Was this article helpful?
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            If you need additional assistance, don't hesitate to contact our support team.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button variant="outline" data-testid="contact-support">
              <ExternalLink className="w-4 h-4 mr-2" />
              Contact Support
            </Button>
            <Button variant="outline" onClick={() => setLocation("/help")} data-testid="browse-more-articles">
              <BookOpen className="w-4 h-4 mr-2" />
              Browse More Articles
            </Button>
          </div>
        </motion.div>
      </div>
    </div>
    <Footer />
  </>
  );
}